<script setup lang="ts">
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue';
import { useI18N } from '@dialpad/i18n';
import { DtInput } from '@dialpad/dialtone/vue3/lib/input';
import { DtSelectMenu } from '@dialpad/dialtone/vue3/lib/select-menu';
import { DtCheckbox } from '@dialpad/dialtone/vue3/lib/checkbox';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { DtStack } from '@dialpad/dialtone/vue3/lib/stack';
import DateTime from '@/app/components/DateTime/DateTime.vue';
import { DATA_QA } from './phone-edit.constants';
import { usePhoneStore } from '@/stores';
import { PhoneDncScopeEnum } from '@/apis/generated/dialbunny';
import type { PhonePost } from '@/apis/generated/dialbunny';
import { closeSidebar } from '@/app/components/sidebar/sidebar';
import { MODAL, openModal } from '@/app/components/modal/modals';
import logger from '@/logger';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import { showAutoDismissToast, showErrorToast } from '@/noto';
import { useRouter } from 'vue-router';

const log = logger.getChild('PhoneEdit');
log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

const router = useRouter();

const validationTrigger = ref(0);
provide(TRIGGER_FORM_VALIDATION, validationTrigger);

const hasValidationError = ref(false);

type Props = {
  e164?: string | null;
};

const props = withDefaults(defineProps<Props>(), {
  e164: null,
});

const { $t } = useI18N();

const phoneStore = usePhoneStore();

const phone = phoneStore.watch(() => props.e164, 'PhoneEdit');

const phoneNumber = ref('');
const dnc = ref(true);
const dncScope = ref<PhoneDncScopeEnum>(PhoneDncScopeEnum.Powerdialer);
const dncExpires = ref(false);
const dncExpirationDate = ref<Date | null>(null);
const dncOptOutDate = ref<Date | null>(null);
const dnt = ref(false);
const dntScope = ref<PhoneDncScopeEnum>(PhoneDncScopeEnum.Powerdialer);
const dntExpires = ref(false);
const dntExpirationDate = ref<Date | null>(null);
const dntOptOutDate = ref<Date | null>(null);

watch(
  () => phone.value.item,
  // eslint-disable-next-line complexity, max-statements
  (value) => {
    if (!value) return;
    phoneNumber.value = value.e164 ?? '';
    dncScope.value = value.dncScope ?? PhoneDncScopeEnum.Powerdialer;
    dnc.value = value.dnc ?? true;
    if (value.dateDncExpires) {
      dncExpires.value = true;
      dncExpirationDate.value = new Date(value.dateDncExpires);
    } else {
      dncExpires.value = false;
      dncExpirationDate.value = null;
    }
    dncOptOutDate.value = value.dateDncOptOut
      ? new Date(value.dateDncOptOut)
      : null;
    dnt.value = value.dnt ?? false;
    dntScope.value = value.dntScope ?? PhoneDncScopeEnum.Powerdialer;
    if (value.dateDntExpires) {
      dntExpires.value = true;
      dntExpirationDate.value = new Date(value.dateDntExpires);
    } else {
      dntExpires.value = false;
      dntExpirationDate.value = null;
    }
    dntOptOutDate.value = value.dateDntOptOut
      ? new Date(value.dateDntOptOut)
      : null;
  },
  { immediate: true },
);

watch(dnc, (value) => {
  if (value) return;
  dncExpires.value = false;
});

watch(dnt, (value) => {
  if (value) return;
  dntExpires.value = false;
});

const scopeOptions = computed(() => [
  {
    value: PhoneDncScopeEnum.Powerdialer,
    label: $t('PHONE_EDIT_SCOPE_CAMPAIGNS_ONLY'),
  },
  { value: PhoneDncScopeEnum.Ever, label: $t('PHONE_EDIT_SCOPE_ALL') },
]);

const enablementOptions = computed(() => [
  { value: 'true', label: $t('PHONE_EDIT_ENABLEMENT_TRUE') },
  { value: 'false', label: $t('PHONE_EDIT_ENABLEMENT_FALSE') },
]);

const onDelete = () => {
  void closeSidebar();
  if (!props.e164) {
    log.error('Missing phone e164');
    return;
  }
  void openModal(MODAL.DELETE_DNC_RECORD, { id: props.e164 });
};

const onClose = () => {
  void closeSidebar();
};

const onCancel = () => {
  router.back();
};

// eslint-disable-next-line max-statements
const onSave = async () => {
  if (!props.e164) {
    log.error('Missing phone e164');
    return;
  }

  hasValidationError.value = false;
  validationTrigger.value++;
  await nextTick();

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (hasValidationError.value) {
    log.debug('Validation failed');
    return;
  }

  const payload: PhonePost = {
    e164: props.e164,
    dnc: dnc.value,
    dnt: dnt.value,
  };

  if (dnc.value) {
    payload.dncScope = dncScope.value;
    payload.dateDncExpires = dncExpires.value ? dncExpirationDate.value : null;
    payload.dateDncOptOut = dncOptOutDate.value;
  }

  if (dnt.value) {
    payload.dntScope = dntScope.value;
    payload.dateDntExpires = dntExpires.value ? dntExpirationDate.value : null;
    payload.dateDntOptOut = dntOptOutDate.value;
  }

  phoneStore.update(props.e164, payload, {
    onSuccess: () => {
      router.back();
      void showAutoDismissToast({
        content: {
          title: {
            key: 'PHONE_EDIT_UPDATE_SUCCESS_TITLE',
          },
        },
      });
    },
    onError: (error) => {
      log.error('Failed to update phone', error);
      void showErrorToast({
        content: {
          title: {
            key: 'PHONE_EDIT_UPDATE_ERROR_TITLE',
          },
        },
      });
    },
  });
};
</script>

<template>
  <div
    :data-qa="DATA_QA.ROOT"
    :data-qa-loading="phone.loading"
    class="d-w100p d-h100p d-bgc-white d-bar-l"
  >
    <div class="d-w100p d-h100p d-d-flex d-fd-column">
      <div class="d-px16 d-pt24 d-pb8 d-bb d-bc-subtle">
        <div class="d-d-flex d-ai-center d-jc-space-between">
          <h2 class="d-fs-300 d-fw-semibold">{{ $t('PHONE_EDIT_TITLE') }}</h2>
          <DtButton
            :data-qa="DATA_QA.CLOSE_BUTTON"
            kind="muted"
            importance="clear"
            circle
            @click="onClose"
          >
            <template #icon>
              <DtIcon name="close" size="300" />
            </template>
          </DtButton>
        </div>
      </div>
      <DtStack
        class="d-p16 d-g16 d-of-y-auto d-fl-grow1 d-divide-y d-divide-default"
        gap="500"
      >
        <DtInput
          v-model="phoneNumber"
          :data-qa="DATA_QA.PHONE_NUMBER_INPUT"
          :label="$t('PHONE_EDIT_PHONE_NUMBER')"
          :disabled="!!phone.item || phone.loading"
        />
        <div>
          <div class="d-d-flex d-g16 d-ai-flex-end d-pt8">
            <DtSelectMenu
              :data-qa="DATA_QA.DNC_SELECT"
              class="d-fl-grow1"
              :label="$t('PHONE_EDIT_DNC')"
              :options="enablementOptions"
              :disabled="phone.loading"
              @change="dnc = $event === 'true'"
            >
              <option
                v-for="option in enablementOptions"
                :key="option.value"
                :value="option.value"
                :selected="option.value === dnc.toString()"
              >
                {{ option.label }}
              </option>
            </DtSelectMenu>
            <div class="d-pb4">
              <DtCheckbox
                v-model="dncExpires"
                :data-qa="DATA_QA.DNC_EXPIRES_CHECKBOX"
                :disabled="!dnc || phone.loading"
              >
                {{ $t('PHONE_EDIT_EXPIRES') }}
              </DtCheckbox>
            </div>
          </div>
          <DateTime
            v-if="dnc"
            v-model="dncOptOutDate"
            :data-qa="DATA_QA.DNC_OPT_OUT_DATE"
            :disabled="phone.loading"
            class="d-pt8"
            :date-label="$t('PHONE_EDIT_DNC_OPT_OUT_DATE')"
            @update:error="hasValidationError = hasValidationError || !!$event"
          />
          <DateTime
            v-if="dnc && dncExpires"
            v-model="dncExpirationDate"
            :data-qa="DATA_QA.DNC_EXPIRATION_DATE"
            :disabled="phone.loading"
            class="d-pt8"
            required
            :date-label="$t('PHONE_EDIT_DNC_EXPIRATION')"
            @update:error="hasValidationError = hasValidationError || !!$event"
          />
          <div v-if="dnc" class="d-pt8">
            <DtSelectMenu
              :data-qa="DATA_QA.DNC_SCOPE_SELECT"
              :label="$t('PHONE_EDIT_DNC_SCOPE')"
              :options="scopeOptions"
              :disabled="phone.loading"
              @change="dncScope = $event"
            >
              <option
                v-for="option in scopeOptions"
                :key="option.value"
                :value="option.value"
                :selected="option.value === dncScope"
              >
                {{ option.label }}
              </option>
            </DtSelectMenu>
          </div>
        </div>
        <div>
          <div class="d-d-flex d-g16 d-ai-flex-end d-pt8">
            <DtSelectMenu
              :data-qa="DATA_QA.DNT_SELECT"
              :model-value="dnt.toString()"
              class="d-fl-grow1"
              :label="$t('PHONE_EDIT_DNT')"
              :options="enablementOptions"
              :disabled="phone.loading"
              @change="dnt = $event === 'true'"
            >
              <option
                v-for="option in enablementOptions"
                :key="option.value"
                :value="option.value"
                :selected="option.value === dnt.toString()"
              >
                {{ option.label }}
              </option>
            </DtSelectMenu>
            <div class="d-pb4">
              <DtCheckbox
                v-model="dntExpires"
                :data-qa="DATA_QA.DNT_EXPIRES_CHECKBOX"
                :disabled="!dnt || phone.loading"
              >
                {{ $t('PHONE_EDIT_EXPIRES') }}
              </DtCheckbox>
            </div>
          </div>
          <DateTime
            v-if="dnt"
            v-model="dntOptOutDate"
            :data-qa="DATA_QA.DNT_OPT_OUT_DATE"
            class="d-pt8"
            :date-label="$t('PHONE_EDIT_DNT_OPT_OUT_DATE')"
            :disabled="phone.loading"
            @update:error="hasValidationError = hasValidationError || !!$event"
          />
          <DateTime
            v-if="dnt && dntExpires"
            v-model="dntExpirationDate"
            :data-qa="DATA_QA.DNT_EXPIRATION_DATE"
            class="d-pt8"
            required
            :date-label="$t('PHONE_EDIT_DNT_EXPIRATION')"
            :disabled="phone.loading"
            @update:error="hasValidationError = hasValidationError || !!$event"
          />
          <div v-if="dnt" class="d-pt8">
            <DtSelectMenu
              :data-qa="DATA_QA.DNT_SCOPE_SELECT"
              :label="$t('PHONE_EDIT_DNT_SCOPE')"
              :options="scopeOptions"
              :disabled="phone.loading"
              @change="dntScope = $event"
            >
              <option
                v-for="option in scopeOptions"
                :key="option.value"
                :value="option.value"
                :selected="option.value === dntScope"
              >
                {{ option.label }}
              </option>
            </DtSelectMenu>
          </div>
        </div>
      </DtStack>
      <div
        class="d-d-flex d-jc-space-between d-ai-center d-p16 d-bt d-bc-subtle"
      >
        <DtButton
          :data-qa="DATA_QA.DELETE_BUTTON"
          kind="danger"
          importance="clear"
          :disabled="phone.loading"
          @click="onDelete"
        >
          {{ $t('PHONE_EDIT_DELETE') }}
        </DtButton>
        <div class="d-d-flex d-g8">
          <DtButton
            :data-qa="DATA_QA.CANCEL_BUTTON"
            kind="muted"
            importance="clear"
            @click="onCancel"
          >
            {{ $t('PHONE_EDIT_CANCEL') }}
          </DtButton>
          <DtButton
            :data-qa="DATA_QA.SAVE_BUTTON"
            :loading="phone.item && phone.loading"
            :disabled="phone.loading"
            @click="onSave"
          >
            {{ $t('PHONE_EDIT_SAVE') }}
          </DtButton>
        </div>
      </div>
    </div>
  </div>
</template>
